import { isArray } from "lodash-es";
import type { Events } from "vue";
import { computed, ref, useAttrs } from "vue";

import { isNonPrimitive } from "@/utils/type";

/**
 *  Provides information about currently processing async event handlers.
 *
 *  const { attrs, processing } = useAsyncHandler("onClick")
 *  async function onClick() {...}
 *
 *  <root-element v-bind="attrs" @click="onClick">
 *    Click handler is running: {{ processing['onClick'] }}
 */
export function useAsyncHandler<T extends Array<keyof Events>>(delay: number, ...eventNames: T) {
  type EventName = (typeof eventNames)[number];

  const $attrs = useAttrs();
  const attrs = computed(() => ({
    ...$attrs,
    ...Object.fromEntries(
      eventNames.map((name) => [name, (event: Event) => handleEvent(name, $attrs[name], event)]),
    ),
  }));

  const processing = ref(Object.fromEntries(eventNames.map((name) => [name, false])));

  function handleEvent(name: EventName, handler: unknown, event: Event) {
    if (typeof handler === "function") {
      doHandleEvent(name, handler(event));
    } else if (isArray(handler)) {
      handler.forEach((handle) => handleEvent(name, handle, event));
    }
  }

  function doHandleEvent(name: EventName, result: unknown) {
    if (isNonPrimitive(result) && "finally" in result && typeof result.finally === "function") {
      const timeout = window.setTimeout(() => (processing.value[name] = true), delay);
      result.finally(() => {
        clearTimeout(timeout);
        processing.value[name] = false;
      });
    }
  }

  return { attrs, processing };
}
